import React, { useState } from 'react';
import { ChevronDown, Lightbulb, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { aud, fmtDate } from './utils';

const Row = ({ s, onConfirm, onDismiss, confirmLabel }) => (
  <tr className="border-t border-[#F0ECE4] transition-colors hover:bg-[#FDFBF7]" data-testid={`suggestion-row-${s.invoice_id}`}>
    <td className="px-4 py-3 font-medium text-[#3A3530]">{s.client_name}</td>
    <td className="px-4 py-3 text-[#6B6560]">{s.assessment_type}</td>
    <td className="px-4 py-3 text-[#6B6560]">#{s.invoice_id}<div className="text-xs text-[#9B9590]">{fmtDate(s.invoice_date)}</div></td>
    <td className="px-4 py-3 text-right tabular-nums">{aud(s.invoiced)}</td>
    <td className="px-4 py-3 text-right tabular-nums text-[#4A6B2E]">{aud(s.paid)}</td>
    <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#3A3530]">{aud(s.balance)}</td>
    <td className="px-4 py-3 text-[#6B6560]">{fmtDate(s.last_payment_date)}</td>
    <td className="px-4 py-3">
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={() => onConfirm(s)} className="bg-[#8B9D83] text-white hover:bg-[#6B7D63]" data-testid={`suggestion-confirm-btn-${s.invoice_id}`}>
          {confirmLabel}
        </Button>
        {onDismiss && (
          <Button size="sm" variant="ghost" onClick={() => onDismiss(s)} className="text-[#9B9590] hover:text-[#C17B6B]" data-testid={`suggestion-dismiss-btn-${s.invoice_id}`}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </td>
  </tr>
);

const Table = ({ items, ...rest }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[860px] text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-[#9B9590]">
          {['Client', 'Assessment', 'Invoice', 'Invoiced', 'Paid', 'Balance', 'Last payment', ''].map((h, i) => (
            <th key={h || i} className={`px-4 py-2 font-semibold ${[3, 4, 5].includes(i) ? 'text-right' : ''}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{items.map((s) => <Row key={s.invoice_id} s={s} {...rest} />)}</tbody>
    </table>
  </div>
);

export const SuggestionsPanel = ({ suggestions, awaiting, onConfirm, onDismiss, onManual }) => {
  const [openAwaiting, setOpenAwaiting] = useState(false);
  return (
    <section className="rounded-2xl border border-[#E8E4DC] bg-white" data-testid="suggestions-section">
      <div className="flex flex-col gap-3 border-b border-[#F0ECE4] p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-[#D4A574]" />
            <h2 className="font-heading text-xl font-semibold text-[#3A3530]">Suggested plans</h2>
            <span className="rounded-full bg-[#D4A574]/20 px-2 py-0.5 text-xs font-bold text-[#8B6B4A]" data-testid="suggestions-count">{suggestions.length}</span>
          </div>
          <p className="mt-1 text-sm text-[#6B6560]">Assessment invoices in Halaxy with a deposit or part-payment and a balance still owing.</p>
        </div>
        <Button variant="outline" onClick={onManual} className="border-[#8B9D83] text-[#6B7D63] hover:bg-[#8B9D83] hover:text-white" data-testid="add-plan-manual-btn">
          <Plus className="mr-1.5 h-4 w-4" />Add plan manually
        </Button>
      </div>
      {suggestions.length ? (
        <Table items={suggestions} onConfirm={onConfirm} onDismiss={onDismiss} confirmLabel="Confirm plan" />
      ) : (
        <p className="p-6 text-sm text-[#9B9590]" data-testid="suggestions-empty">No new part-paid assessment invoices. All caught up.</p>
      )}
      <Collapsible open={openAwaiting} onOpenChange={setOpenAwaiting} className="border-t border-[#F0ECE4]">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium text-[#6B6560] hover:bg-[#FDFBF7]" data-testid="awaiting-deposit-toggle">
          <span>Awaiting deposit · {awaiting.length} invoice{awaiting.length === 1 ? '' : 's'} with $0 paid</span>
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openAwaiting ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent data-testid="awaiting-deposit-list">
          <Table items={awaiting} onConfirm={onConfirm} onDismiss={onDismiss} confirmLabel="Set up plan" />
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};
